import React, { FC, useContext, useEffect, useState } from 'react';

import {
  Collapse,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core';

import { GlobalContext } from '../../../../store';
import { IMetaFormat, TIFormatPrefix } from '../../../../store/admin/roundFormats/store';
import { IResult } from '../../../../store/admin/roundResults/store';

import { groupBy } from '../../../../contrib/utils';
import {
  useAssignResultsDispatch,
  useAssignResultsState
} from '../../../admin/EventWizard/Common/AssignResults/Context/AssignResults.context';
import { getResultFieldName } from '../../../admin/EventWizard/Common/ResultsTable/Utils/getResultFieldName';

import { StillInRaceUserInfo } from './StillInRaceUserInfo';
import { useEventLeaderboardsState } from '../Context/EventLeaderboards.context';

interface IProps {
  flightId: number;
  roundId?: number;
}

const RaceTable: FC<IProps> = props => {
  const { flightId, roundId } = props;

  const [collapsedRow, setCollapsedRow] = useState<null | number>(null);

  const { roundResults } = useContext(GlobalContext);

  const {
    horseRaceEliminatedResults,
    results,
    roundFormatsList,
    selectedFormat,
    selectedScoreType,
    sortedResults
  } = useAssignResultsState();

  const { selectedRound } = useEventLeaderboardsState();

  const [scoreFieldName, setScoreFieldName] = useState<keyof IResult | undefined>();

  const [selectedRoundFormat, setSelectedRoundFormat] = useState<IMetaFormat | undefined>();

  const {
    dispatch: dispatchAssignResults,
    actions: { setHorseRaceEliminatedResults }
  } = useAssignResultsDispatch();

  useEffect(() => {
    setCollapsedRow(null);
  }, [roundId, flightId, selectedScoreType]);

  const onClick = (rowIndex: number) => () =>
    setCollapsedRow(collapsedRow === rowIndex ? null : rowIndex);

  const [resultsByHole, setResultsByHole] = useState<{ [index: string]: IResult[] }>({});

  useEffect(() => {
    if (
      selectedRoundFormat &&
      !selectedRoundFormat.considerFlights &&
      Object.keys(roundResults).length > 0
    ) {
      const selectedRoundId = selectedRound ? Number(selectedRound.id) : -1;
      const selectedRoundResults = roundResults[selectedRoundId];

      const newResults = selectedRoundResults ? Object.values(selectedRoundResults).flat() : [];

      dispatchAssignResults({
        type: 'setResults',
        value: newResults
      });
    }
  }, [selectedRound, roundResults, selectedRoundFormat]);

  useEffect(() => {
    dispatchAssignResults({ type: 'sortResults' });
  }, [results, selectedScoreType]);

  useEffect(() => {
    if (selectedFormat && selectedScoreType) {
      setSelectedRoundFormat(
        Object.values(roundFormatsList).find(
          (roundFormat: IMetaFormat) =>
            roundFormat.scoreTypes.includes(selectedScoreType) &&
            roundFormat.format.id === selectedFormat.id
        )
      );
    }
  }, [selectedScoreType, selectedFormat, roundFormatsList]);

  useEffect(() => {
    if (selectedRoundFormat && selectedScoreType) {
      setHorseRaceEliminatedResults(selectedRoundFormat, selectedScoreType);
    }
  }, [selectedRoundFormat, sortedResults, selectedScoreType]);

  useEffect(() => {
    if (selectedFormat && selectedScoreType) {
      if (selectedRoundFormat) {
        const selectedFormatPrefix = selectedFormat.prefix;

        const processedPrefix = `${selectedFormatPrefix}${
          selectedRoundFormat.considerFlights ? 'Flight' : 'Round'
        }` as TIFormatPrefix;

        const positionFieldName = getResultFieldName(processedPrefix, selectedScoreType, 'Hole');

        setScoreFieldName(getResultFieldName(processedPrefix, selectedScoreType, 'Score'));

        if (horseRaceEliminatedResults.true) {
          setResultsByHole(groupBy(horseRaceEliminatedResults.true, positionFieldName));
        }
      }
    }
  }, [
    results,
    selectedScoreType,
    selectedFormat,
    roundFormatsList,
    horseRaceEliminatedResults,
    selectedRoundFormat,
    sortedResults
  ]);

  return (
    <div className="event-leaderboards-table horseRaceTable">
      {horseRaceEliminatedResults.false && (
        <div className="stillInRace">
          <h2 className="headline">STILL IN THE RACE</h2>

          <Grid container={true} spacing={2}>
            {horseRaceEliminatedResults.false.map(result => (
              <Grid
                className="grid-item"
                item={true}
                xs={6}
                key={`still-in-race-${result.enrollmentId}`}
              >
                <StillInRaceUserInfo result={result} />
              </Grid>
            ))}
          </Grid>
        </div>
      )}

      <Table className="leaderboard-score-table">
        <TableHead>
          <TableRow>
            <TableCell colSpan={3} className="eliminated-head">
              ELIMINATED
            </TableCell>
          </TableRow>
        </TableHead>

        <colgroup>
          {[0, 1, 2].map(index => (
            <col key={index} />
          ))}
        </colgroup>

        <TableHead className="leaderboard__thead">
          <TableRow className="leaderboard-header">
            <TableCell className="leaderboard-header__cell override index">Hole</TableCell>

            <TableCell className="leaderboard-header__cell override playerName">Player</TableCell>

            <TableCell className="leaderboard-header__cell override result">High Score</TableCell>
          </TableRow>
        </TableHead>

        <TableBody className="leaderboard__tbody">
          {resultsByHole && Object.keys(resultsByHole).length > 0 ? (
            Object.keys(resultsByHole)
              .reverse()
              .map((hole: string, index: number) => [
                <TableRow
                  id={`clickableRowResult-${index}`}
                  key={`tableRowResult-${index}`}
                  onClick={onClick(index)}
                >
                  <TableCell className="index">{Number(hole) + 1}</TableCell>

                  <TableCell className="playerName">{`${
                    resultsByHole[hole].length
                  } players eliminated`}</TableCell>

                  <TableCell className="result">
                    {scoreFieldName &&
                      resultsByHole[hole][resultsByHole[hole].length - 1][scoreFieldName]}
                  </TableCell>
                </TableRow>,
                <TableRow className="expandable-row" key={`hiddenTableRow-${index}`}>
                  <TableCell
                    className="inner-cell"
                    colSpan={4}
                    classes={{ root: 'leaderboard-row-event-summary' }}
                  >
                    <Collapse
                      className={`collapse-${index}`}
                      classes={{
                        container: 'leaderboard-collapse',
                        wrapperInner: 'leaderboard-collapse__wrapper-inner override'
                      }}
                      in={collapsedRow === index}
                      timeout="auto"
                      unmountOnExit={true}
                    >
                      {resultsByHole[hole].map((player: any, expandedIndex: number) => (
                        <div
                          className="eliminated-player-row"
                          key={`collapse-item-${expandedIndex}`}
                        >
                          {`${player.userFirstName[0]}. ${player.userLastName}`}
                        </div>
                      ))}
                    </Collapse>
                  </TableCell>
                </TableRow>
              ])
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="no-results">
                No results to show
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default RaceTable;
